import { Box, Stack, Typography } from '@mui/material';
import { formatter } from '@repo/utils';
import { COLORS, RADIUS } from '../../constants';

const blue = COLORS.primary.blue.main;

export type PaymentScheduleItem = {
  number: number | string;
  /** Due date line under the installment name, e.g. "Vence el 07/12/2026" */
  dueLabel: string;
  amount?: number | string;
  /** Filled purple badge and brand due line, for the installment due today */
  highlight?: boolean;
};

export type PaymentScheduleProps = {
  count?: number;
  installmentAmount?: number | string;
  total?: number | string;
  withInterest?: boolean;
  items: PaymentScheduleItem[];
};

/** Pill shown on interest-free plans. */
export const InterestFreePill = () => (
  <Box
    component="span"
    sx={{
      px: 1.25,
      py: 0.25,
      borderRadius: `${RADIUS.full}px`,
      backgroundColor: COLORS.chip.success.background,
      color: COLORS.chip.success.text,
      fontSize: 12,
      lineHeight: '18px',
      fontWeight: 700,
    }}
  >
    Sin interés
  </Box>
);

// Amount with the cents smaller and faded, as on the order card
const SummaryRow = ({
  label,
  value,
  color,
  interestFree,
}: {
  label: string;
  value?: number | string;
  color: string;
  interestFree?: boolean;
}) => {
  const [integer, cents] = formatter(value?.toString(), 'currency').split(',');
  return (
    <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
      <Typography component="span" variant="subtitle2" sx={{ lineHeight: '24px' }}>
        {label}
      </Typography>
      {/* Amount with an optional interest-free pill underneath, both flush right */}
      <Stack sx={{ alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
        <Stack direction="row" sx={{ alignItems: 'flex-start' }}>
          <Typography
            component="span"
            sx={{ typography: 'h2', fontSize: 18, lineHeight: '24px', fontWeight: 700, letterSpacing: '0.01em', color }}
          >
            {integer}
          </Typography>
          {cents && (
            <Typography
              component="span"
              sx={{
                typography: 'h2',
                letterSpacing: '0.01em',
                fontSize: 12,
                lineHeight: '18px',
                fontWeight: 700,
                color,
                // Faded blue still clears 4.5:1; faded purple wouldn't, so purple cents stay solid
                opacity: color === blue ? 0.75 : 1,
              }}
            >
              ,{cents}
            </Typography>
          )}
        </Stack>
        {interestFree && <InterestFreePill />}
      </Stack>
    </Stack>
  );
};

/**
 * Payment schedule of a plan, as in customer-mobile: a summary strip with the installment amount and the total,
 * then every installment as a numbered row with its due date and amount.
 *
 * @param {PaymentScheduleProps} props - Plan summary and its installments, already formatted for display.
 * @return {React.JSX.Element} The rendered PaymentSchedule component.
 */
export const PaymentSchedule = ({ count, installmentAmount, total, withInterest, items }: PaymentScheduleProps) => (
  <Stack sx={{ gap: 3, width: '100%' }}>
    {/* Label/value rows: one amount per line reads lighter than two bold amounts side by side */}
    <Stack
      sx={{
        gap: 1.5,
        px: 2.5,
        py: 2,
        borderRadius: `${RADIUS.lg}px`,
        backgroundColor: COLORS.surface.brandTint,
        textAlign: 'left',
      }}
    >
      <SummaryRow
        label={`${count} ${count === 1 ? 'cuota' : 'cuotas'} de`}
        value={installmentAmount}
        color={COLORS.text.brand}
      />
      <Box sx={{ height: '1px', backgroundColor: COLORS.border.brand }} />
      <SummaryRow label="Total a pagar" value={total} color={blue} interestFree={withInterest === false} />
    </Stack>

    <Box component="ol" aria-label="Cuotas" sx={{ listStyle: 'none', m: 0, p: 0, maxHeight: 320, overflowY: 'auto' }}>
      {items.map(({ number, dueLabel, amount, highlight }) => (
        <Stack
          component="li"
          key={number}
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 2,
            py: 1.5,
            borderBottom: `1px solid ${COLORS.border.faint}`,
            '&:last-of-type': { borderBottom: 'none' },
          }}
        >
          <Box
            aria-hidden
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              width: 32,
              height: 32,
              borderRadius: `${RADIUS.full}px`,
              fontSize: 14,
              fontWeight: 700,
              backgroundColor: highlight ? COLORS.brand.main : COLORS.surface.brandTint,
              color: highlight ? COLORS.text.inverted : COLORS.text.brand,
            }}
          >
            {number}
          </Box>
          <Stack sx={{ flex: 1, textAlign: 'left' }}>
            <Typography
              component="span"
              sx={{ typography: 'h2', letterSpacing: '0.01em', fontSize: 14, fontWeight: 700, color: COLORS.text.main }}
            >
              Cuota {number}
            </Typography>
            <Typography component="span" variant="subtitle2" sx={highlight ? { color: COLORS.text.brand } : {}}>
              {dueLabel}
            </Typography>
          </Stack>
          <Typography
            component="span"
            sx={{ typography: 'h2', letterSpacing: '0.01em', fontSize: 16, fontWeight: 700, color: blue }}
          >
            {formatter(amount?.toString(), 'currency')}
          </Typography>
        </Stack>
      ))}
    </Box>
  </Stack>
);
